import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft, FaShieldAlt, FaUserShield, FaLock, FaGavel, FaEnvelope, FaClock, FaExchangeAlt, FaUser, FaCookie, FaExclamationTriangle, FaEdit, FaTimesCircle } from 'react-icons/fa';

const TermsContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const TermsCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Header = styled.div`
  margin-bottom: 3rem;
  margin-top: 1rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 2px;
    background: #FDBF37;
    border-radius: 2px;
  }
  
  h1 {
    color: #FDBF37;
    margin: 0;
    font-size: 1.8rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    
    @media (min-width: 768px) {
      font-size: 2.2rem;
    }
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #FDBF37;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  background: transparent;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
  
  &:hover {
    background: #fff5f0;
    color: #e53e3e;
  }
  
  @media (min-width: 768px) {
    width: fit-content;
    font-size: 1rem;
  }
`;

const TermsContent = styled.div`
  line-height: 1.7;
  color: #4a5568;
  font-size: 0.95rem;
  text-align: left;
  
  h3 {
    color: #FDBF37;
    margin-top: 2.5rem;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &:first-of-type {
      margin-top: 0;
    }
  }
  
  h4 {
    color: #FDBF37;
    margin-top: 1.5rem;
    margin-bottom: 0.8rem;
    font-size: 1rem;
    font-weight: 600;
  }
  
  p {
    margin-bottom: 1rem;
    text-align: justify;
    font-size: 0.95rem;
    line-height: 1.7;
  }
  
  ul {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
    list-style: disc;
  }
  
  li {
    margin-bottom: 0.5rem;
    color: #4a5568;
    line-height: 1.6;
  }
  
  .company-info {
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 8px;
    border-left: 4px solid #FDBF37;
    margin: 1.5rem 0;
  }
  
  .legal-section {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e2e8f0;
  }
`;

const SectionIcon = styled.span`
  color: #f99d2c;
  font-size: 1rem;
`;

const LastUpdated = styled.p`
  margin-top: 2rem !important;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 6px;
  border-left: 3px solid #FDBF37;
  font-style: italic;
  color: #718096 !important;
  text-align: center !important;
  font-size: 0.9rem;
`;

const TermsAndConditions: React.FC = () => {
  return (
    <TermsContainer>
      <TermsCard>
        <BackButton to="/register">
          <FaArrowLeft />
          Volver al registro
        </BackButton>
        <Header>
          <h1>Términos y Condiciones</h1>
        </Header>
        
        <TermsContent>
          <div className="company-info">
            <p>
              <strong>Vargas Homecenter</strong>, identificado con RUC Nº 20536557858, con domicilio en 
              Av. Aviación 2405, piso 5, distrito de San Borja, Lima, Perú, realiza el tratamiento de los datos 
              personales de las personas que contactan con nosotros a través del uso de sitios web, aplicaciones 
              móviles, teléfono, redes sociales y/o cualquier otro canal de contacto y/o contratación.
            </p>
          </div>
          
          <h3>
            <SectionIcon><FaGavel /></SectionIcon>
            1. Aceptación de los Términos
          </h3>
          <p>
            Al crear una cuenta en nuestra plataforma, usted acepta cumplir con estos términos y condiciones. 
            Para estos efectos, haremos referencia al "Titular", como aquella persona cuyos datos serán 
            recopilados y tratados de acuerdo a los términos establecidos en este documento. Únicamente son 
            tratados los datos personales para los cuales se ha obtenido el debido consentimiento, salvo sean 
            aplicables las excepciones establecidas en la Ley Nº 29733, Ley de Protección de Datos Personales.
          </p>
          
          <h3>
            <SectionIcon><FaShieldAlt /></SectionIcon>
            2. Marco Normativo
          </h3>
          <p>
            Estas condiciones de tratamiento de datos se encuentran reguladas por la legislación peruana 
            en materia de protección de datos personales:
          </p>
          <ul>
            <li>Ley No. 29733, Ley de Protección de Datos Personales</li>
            <li>Decreto Supremo No. 016-2024-JUS, que aprueba el Reglamento de la Ley No. 29733</li>
            <li>Directiva de Seguridad de la Información, aprobada por la Resolución Directoral No. 019-2013-JUS/DGPDP</li>
          </ul>
          
          <h3>
            <SectionIcon><FaLock /></SectionIcon>
            3. Bancos de Datos
          </h3>
          <p>
            La información recopilada será almacenada en los siguientes bancos de datos personales inscritos 
            en el Registro Nacional de Protección de Datos Personales de la Dirección de Protección de Datos 
            Personales del MINJUS de titularidad de la Empresa: "Clientes", inscrito bajo el código N° RNPDP-PJP N°1401.
          </p>
          
          <h3>
            <SectionIcon><FaUserShield /></SectionIcon>
            4. Finalidad
          </h3>
          
          <h4>4.1 Banco de datos "Clientes"</h4>
          <p>
            El banco de datos "Clientes" almacena los datos personales siguientes: Nombres y apellidos, DNI, 
            dirección, correos electrónicos, teléfono, firma, carné de extranjería, RUC, fecha de nacimiento, 
            nacionalidad, sexo, profesión, edad, historial de compras. Los datos son recopilados a través de: 
            Formularios, transmisión electrónica, encuestas, telemarketing, entrevistas personales.
          </p>
          
          <h4>Finalidad Necesaria:</h4>
          <p>
            Los datos personales proporcionados son de carácter necesario y serán tratados por la Empresa para 
            gestionar las solicitudes y/o contratos de servicios de despacho a domicilio, servicio de retiro en 
            tienda, servicio técnico y servicios generales tales como armados de muebles e instalación de productos. 
            Se podrá igualmente realizar, de manera anonimizada o disociada, análisis estadístico para la elaboración 
            de estrategias comerciales.
          </p>
          
          <p>
            Para la realización de las finalidades necesarias, se pueden incluir en el tratamiento de los datos 
            decisiones automatizadas, cuyos resultados podrán tener un impacto en las acciones o servicios ofrecidos 
            tales como bots de respuesta, reperfilamiento de clientes, siempre garantizando el cumplimiento de las 
            normativas aplicables y la protección de sus derechos.
          </p>
          
          <p>
            Cabe precisar que para cumplir con las finalidades necesarias es posible que se transfieran datos 
            personales a las entidades listadas en la sección "Transferencias y destinatarios" del presente documento.
          </p>
          
          <p>
            En caso el Titular no proporcione sus datos personales obligatorios para las finalidades necesarias, 
            la Empresa no podrá realizar la finalidad antes descrita.
          </p>
          
          <h4>4.2 Banco de datos "Clientes" - Finalidad Opcional (requiere consentimiento expreso)</h4>
          <p>
            De manera opcional, sus datos personales: Nombres y apellidos, DNI, dirección, correos electrónicos, 
            teléfono, firma, carné de extranjería, RUC, fecha de nacimiento, nacionalidad, sexo, profesión, edad, 
            historial de compras, podrán ser utilizados por la Empresa con fines comerciales, previo consentimiento 
            expreso de su parte. Estas finalidades opcionales incluyen:
          </p>
          
          <ul>
            <li>Realizar estudios de mercado, obtención de información a través de fuentes públicas y privadas, 
            perfiles de compra y envío de publicidad, promociones y ofertas de productos y/o servicios y/o de 
            otras empresas con ocasión de alianzas comerciales, mediante correo electrónico, llamadas telefónicas, 
            mensajería instantánea u otros canales que se haya habilitado para dicho fin.</li>
            
            <li>Compartir con las empresas del Grupo Intercorp y/o sus socios comerciales sus datos personales, 
            con la finalidad de realizar estudios de mercado, perfiles de compra, envío de publicidad, promociones 
            y ofertas de sus productos y/o servicios que estas ofrecen.</li>
          </ul>
          
          <p>
            Es importante mencionar que, para las finalidades opcionales, la Empresa podrá compartir sus datos 
            personales con socios comerciales y empresas pertenecientes al Grupo Intercorp en los rubros de retail, 
            restaurantes, entretenimiento, hoteles e inmobiliarias, salud, seguros, educación y financieras.
          </p>
          
          <p>
            La negativa de otorgar su consentimiento para estas finalidades opcionales de tratamiento no afecta 
            el cumplimiento de las obligaciones contractuales ni impide el tratamiento para las finalidades 
            necesarias precisadas previamente en esta Política de Privacidad.
          </p>
          
          <p>
            Para la realización de la finalidad opcional, se podrán realizar decisiones automatizadas, incluida 
            la elaboración de perfiles, cuyos resultados podrán tener un impacto en las acciones ofrecidas, tales 
            como brindarle descuentos personalizados sobre la base de la elaboración de su perfil de consumo, 
            siempre garantizando el cumplimiento de las normativas aplicables y la protección de sus derechos.
          </p>
          
          <h3>
            <SectionIcon><FaClock /></SectionIcon>
            5. Plazo de conservación
          </h3>
          <p>
            Los datos personales proporcionados por el Titular se conservarán en los bancos de datos de la Empresa 
            en tanto sean necesarios para cumplir con la finalidad para la cual fueron recopilados (por ejemplo, 
            ejecutar un contrato, atender un reclamo) y/o mientras el Titular no revoque su consentimiento (en caso 
            haya brindado su consentimiento para la finalidad opcional). En caso surjan las situaciones descritas, 
            le comunicamos que sus datos personales podrán permanecer bloqueados (sin ser objeto de tratamiento) 
            por el plazo de prescripción legal para asegurar nuestro debido ejercicio del derecho de defensa, 
            conforme a lo regulado por la Ley y normativa aplicable.
          </p>
          
          <h3>
            <SectionIcon><FaExchangeAlt /></SectionIcon>
            6. Transferencia y destinatarios
          </h3>
          <p>
            Para cumplir con las finalidades necesarias y, de ser el caso, las opcionales, la Empresa podrá tratar 
            datos de manera directa o a través de proveedores (encargados de tratamiento) o requerir comunicar los 
            datos personales a otras empresas o entidades. Estos terceros se encuentran obligados a brindar el 
            servicio de acuerdo con nuestras directrices de protección de datos y/o con la normativa peruana aplicable.
          </p>
          
          <p>
            Se informa al Titular que sus datos personales son compartidos con terceros, cuyo detalle se encuentra 
            en el siguiente enlace Relación de Proveedores.
          </p>
          
          <p>
            Contamos con proveedores de servicios y socios estratégicos que tienen presencia en el extranjero. 
            En estos casos, algunas actividades o procesamiento de datos personales pueden llevarse a cabo fuera 
            de Perú.
          </p>
          
          <p>
            Queremos asegurarle que hemos implementado los procedimientos necesarios para garantizar que estas 
            empresas también cumplan con las normas de protección de datos personales peruana y la legislación 
            aplicable en el país donde se realiza el tratamiento. Además, nos comprometemos a informar oportunamente 
            a la Autoridad de Protección de Datos Personales aquellos flujos transfronterizos conforme lo exige 
            nuestra normativa vigente.
          </p>
          
          <h3>
            <SectionIcon><FaUserShield /></SectionIcon>
            7. Ejercicio de derechos
          </h3>
          <p>
            El Titular podrá ejercer los derechos de acceso, portabilidad, rectificación cancelación, y oposición 
            de los datos personales; así como revocar el consentimiento para las finalidades opcionales y no 
            necesarias para la ejecución de la relación que lo vincule con la Empresa, a través del siguiente 
            formulario: Derecho Arco o en nuestras oficinas ubicadas en la dirección señalada líneas arriba.
          </p>
          
          <p>
            De considerar que no ha sido atendido en el ejercicio de los derechos de manera adecuada puede presentar 
            una reclamación ante la Autoridad Nacional de Protección de Datos Personales, dirigiéndose a la Mesa 
            de Partes del Ministerio de Justicia y Derechos Humanos: Calle Scipión Llona 350, Miraflores, Lima, 
            Perú o llenando el Formulario N° 011 - Procedimiento trilateral de tutela (por denegatoria del ejercicio 
            de los derechos de acceso, rectificación, cancelación y oposición como titular de datos personales), 
            que figura en la página web del Ministerio de Justicia y Derechos Humanos.
          </p>
          
          <h3>
            <SectionIcon><FaLock /></SectionIcon>
            8. Resguardo de la información
          </h3>
          <p>
            La Empresa ha adoptado las medidas de seguridad necesarias para garantizar la protección de los datos 
            personales proporcionados por el Titular a fin de evitar su alteración, pérdida, acceso o procesamiento 
            no autorizado por parte de terceros que puedan afectar la información de carácter personal.
          </p>
          
          <h3>
            <SectionIcon><FaLock /></SectionIcon>
            9. Seguridad de Datos
          </h3>
          <p>
            Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger 
            sus datos personales contra el acceso no autorizado, alteración, divulgación o destrucción.
            Utilizamos protocolos de encriptación SSL/TLS para todas las transmisiones de datos sensibles 
            y mantenemos nuestros sistemas actualizados con los últimos parches de seguridad.
          </p>
          
          <h3>
            <SectionIcon><FaUser /></SectionIcon>
            10. Responsabilidades del Usuario
          </h3>
          <p>
            Usted se compromete a:
          </p>
          <ul>
            <li>Proporcionar información veraz y actualizada en todos los campos del registro</li>
            <li>Mantener la confidencialidad de sus credenciales de acceso y no compartirlas con terceros</li>
            <li>Utilizar la plataforma de manera responsable y legal, respetando los derechos de otros usuarios</li>
            <li>Notificar inmediatamente cualquier uso no autorizado de su cuenta</li>
            <li>Actualizar su información personal cuando sea necesario</li>
          </ul>
          
          <h3>
            <SectionIcon><FaShieldAlt /></SectionIcon>
            11. Política de Privacidad
          </h3>
          <p>
            Su privacidad es importante para nosotros. No compartimos su información personal 
            con terceros sin su consentimiento explícito, excepto cuando sea estrictamente necesario para:
          </p>
          <ul>
            <li>Procesar sus pedidos y facilitar las transacciones</li>
            <li>Cumplir con requisitos legales y regulatorios</li>
            <li>Proteger nuestros derechos legales y los de nuestros usuarios</li>
            <li>Prevenir fraudes y actividades maliciosas</li>
          </ul>
          
          <h3>
            <SectionIcon><FaCookie /></SectionIcon>
            12. Cookies y Tecnologías de Seguimiento
          </h3>
          <p>
            Utilizamos cookies y tecnologías similares para mejorar su experiencia en nuestra plataforma, 
            analizar el tráfico del sitio web y personalizar el contenido. Puede gestionar sus preferencias 
            de cookies a través de la configuración de su navegador.
          </p>
          
          <h3>
            <SectionIcon><FaExclamationTriangle /></SectionIcon>
            13. Limitación de Responsabilidad
          </h3>
          <p>
            Nuestra plataforma se proporciona "tal como está" y no garantizamos que esté libre de errores 
            o interrupciones. No seremos responsables de daños indirectos, incidentales o consecuentes 
            que puedan surgir del uso de nuestros servicios.
          </p>
          
          <h3>
            <SectionIcon><FaEdit /></SectionIcon>
            14. Modificaciones de los Términos
          </h3>
          <p>
            Nos reservamos el derecho de modificar estos términos en cualquier momento. 
            Las modificaciones entrarán en vigor inmediatamente después de su publicación 
            en la plataforma. Le notificaremos sobre cambios significativos a través del 
            correo electrónico registrado en su cuenta.
          </p>
          
          <h3>
            <SectionIcon><FaTimesCircle /></SectionIcon>
            15. Terminación de la Cuenta
          </h3>
          <p>
            Tanto usted como nosotros podemos terminar su cuenta en cualquier momento. 
            En caso de terminación, sus datos personales serán eliminados de acuerdo con 
            nuestra política de retención de datos, excepto cuando la ley requiera su conservación.
          </p>
          
          <h3>
            <SectionIcon><FaGavel /></SectionIcon>
            16. Ley Aplicable y Jurisdicción
          </h3>
          <p>
            Estos términos se rigen por las leyes del país donde operamos. 
            Cualquier disputa relacionada con estos términos será resuelta en los 
            tribunales competentes de nuestra jurisdicción.
          </p>
          
          <h3>
            <SectionIcon><FaEnvelope /></SectionIcon>
            17. Contacto
          </h3>
          <p>
            Si tiene preguntas sobre estos términos y condiciones, puede contactarnos 
            a través de nuestros canales de atención al cliente:
          </p>
          <ul>
            <li>Email: soporte@vargashomecenter.com</li>
            <li>Teléfono: +51 XXX XXX XXX</li>
            <li>Dirección: [Dirección de la empresa]</li>
          </ul>
          
          <LastUpdated>
            📅 Última actualización: {new Date().toLocaleDateString('es-ES', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </LastUpdated>
        </TermsContent>
      </TermsCard>
    </TermsContainer>
  );
};

export default TermsAndConditions;